"use client";

import {isClient} from "@/shared/utils/runtime";

import type {AnalyticsPayload} from "./analytics.types";

export type {AnalyticsPayload} from "./analytics.types";

export interface DataLayerEmitter {
    push(data: AnalyticsPayload): void;
}

export interface SegmentTrackEmitter {
    track(eventName: string, properties?: AnalyticsPayload): void;
}

type SegmentAnalytics = {
    track?: (eventName: string, properties?: AnalyticsPayload) => void;
    user?: () => {
        anonymousId?: () => string;
    };
};

export class GtmDataLayerEmitter implements DataLayerEmitter {
    push(data: AnalyticsPayload): void {
        if (!isClient()) return;

        window.dataLayer = window.dataLayer ?? [];
        window.dataLayer.push(data);
    }
}

export class SegmentClientEmitter implements SegmentTrackEmitter {
    track(eventName: string, properties?: AnalyticsPayload): void {
        if (!isClient()) return;

        const analytics = (window as Window & {analytics?: SegmentAnalytics}).analytics;
        analytics?.track?.(eventName, properties);
    }
}

export function createGtmDataLayerEmitter(): DataLayerEmitter {
    return new GtmDataLayerEmitter();
}

export function createSegmentClientEmitter(): SegmentTrackEmitter {
    return new SegmentClientEmitter();
}

function normalizeSegmentAnonymousId(value: string | null | undefined): string | undefined {
    const decodedValue = value ? decodeURIComponent(value).trim() : "";
    if (!decodedValue) return undefined;

    try {
        const parsedValue = JSON.parse(decodedValue) as unknown;
        return typeof parsedValue === "string" ? parsedValue.trim() || undefined : undefined;
    } catch {
        return decodedValue;
    }
}

function getCookieValue(name: string): string | undefined {
    return document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${name}=`))
        ?.slice(name.length + 1);
}

export function getSegmentAnonymousId(): string | undefined {
    if (!isClient()) return undefined;

    const analyticsAnonymousId = (window as Window & {analytics?: SegmentAnalytics}).analytics?.user?.()?.anonymousId?.();
    return (
        normalizeSegmentAnonymousId(analyticsAnonymousId) ??
        normalizeSegmentAnonymousId(window.localStorage.getItem("ajs_anonymous_id")) ??
        normalizeSegmentAnonymousId(getCookieValue("ajs_anonymous_id"))
    );
}
